import React, { useState } from "react";
import HeaderNav from "@/components/HeaderNav";
import AuthModal from "@/components/AuthModal";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Zap, TrendingUp, Users, Sparkles, DollarSign, Clock, CheckCircle2 } from "lucide-react";

const LIVE_TICKER = [
  "Maya cashed out $3,200 from freelance web dev",
  "Devon hit $1,050 MRR on newsletter",
  "Sam sold 42 Etsy templates → $512",
  "Alex booked $1,800 in Airbnb co-hosting",
  "Jordan pulled $2,400 from Notion templates",
  "Priya invoiced $4,600 in bookkeeping retainers",
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);

  const startQuiz = () => {
    if (user) nav("/quiz");
    else setAuthOpen(true);
  };

  return (
    <div className="min-h-screen">
      <HeaderNav/>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-12 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="caption inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[var(--copper)] animate-pulse"></span>
              AI-Matched · Money-First · No Fluff
            </div>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.02]">
              Find the side hustle
              <br/>
              that <span className="italic text-[var(--copper)]">actually</span> pays you.
            </h1>
            <p className="text-lg sm:text-xl text-[var(--stone)] max-w-xl leading-relaxed">
              Take a 60-second quiz. Get personalized matches with real earnings ranges,
              startup costs, and step-by-step AI blueprints written just for you.
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={startQuiz} className="btn-primary text-base" data-testid="hero-start-quiz-btn">
                Start free quiz <ArrowRight size={18}/>
              </button>
              <button onClick={()=>nav("/gigs")} className="btn-outline text-base" data-testid="hero-browse-btn">
                Browse live gigs
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-6 pt-4 text-sm text-[var(--stone)]">
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[var(--forest)]"/> 22+ vetted hustles</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[var(--forest)]"/> Claude-powered guides</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[var(--forest)]"/> Real income tracker</div>
            </div>
          </div>

          {/* Bento */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="card-warm p-6 col-span-2" style={{background:"var(--forest)", color:"var(--alabaster)"}}>
              <div className="caption" style={{color:"#D96B27"}}>This week</div>
              <div className="font-display text-4xl sm:text-5xl font-black mt-2">$47,382</div>
              <div className="text-sm opacity-80 mt-1">earned by ajobforsure members</div>
            </div>
            <div className="card-warm p-5">
              <TrendingUp className="text-[var(--copper)] mb-2" size={22}/>
              <div className="font-display text-2xl font-bold">$3,240<span className="text-sm text-[var(--stone)]">/mo</span></div>
              <div className="text-xs text-[var(--stone)]">avg member income</div>
            </div>
            <div className="card-warm p-5">
              <Clock className="text-[var(--copper)] mb-2" size={22}/>
              <div className="font-display text-2xl font-bold">11h<span className="text-sm text-[var(--stone)]">/wk</span></div>
              <div className="text-xs text-[var(--stone)]">typical time invested</div>
            </div>
            <div className="card-warm p-5 col-span-2">
              <div className="caption mb-2">Top match this month</div>
              <div className="font-display text-xl font-bold">Newsletter Publishing</div>
              <div className="flex items-center justify-between mt-3">
                <div className="text-xs text-[var(--stone)]">$200–$20k/mo · 5–12 hrs/wk</div>
                <span className="match-badge">92% match</span>
              </div>
            </div>
          </div>
        </div>

        {/* Live ticker */}
        <div className="border-y border-[var(--border-warm)] bg-white py-3 overflow-hidden">
          <div className="marquee-track">
            {[...LIVE_TICKER, ...LIVE_TICKER].map((t, i) => (
              <div key={`${t}-${i}`} className="flex items-center gap-2 text-sm">
                <DollarSign size={14} className="text-[var(--copper)]"/>
                <span className="font-mono">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
        <div className="caption text-center mb-4">How it works</div>
        <h2 className="font-display text-3xl sm:text-5xl font-black text-center max-w-3xl mx-auto leading-tight">
          Stop guessing which side hustle fits you.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            { i: Zap, t: "Take the quiz", d: "60 seconds. Skills, time, income goal, experience." },
            { i: Sparkles, t: "Get matched", d: "AI ranks 22+ real hustles by earnings, effort, and fit." },
            { i: TrendingUp, t: "Launch with a blueprint", d: "Claude writes your Phase 1 → First $500 playbook." },
          ].map(({i:Icon,t,d}, idx) => (
            <div key={t} className="card-warm p-8">
              <div className="w-12 h-12 rounded-2xl bg-[var(--alabaster-2)] flex items-center justify-center mb-4">
                <Icon size={22} className="text-[var(--forest)]"/>
              </div>
              <div className="caption">Step {idx+1}</div>
              <h3 className="font-display text-2xl font-bold mt-2">{t}</h3>
              <p className="text-[var(--stone)] mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial band */}
      <section className="bg-[var(--forest)] text-[var(--alabaster)] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <div className="caption" style={{color:"#D96B27"}}>What members say</div>
          <blockquote className="font-display text-2xl sm:text-4xl font-medium mt-4 leading-tight">
            "Quit the guessing game. Went from doom-scrolling side hustle Reddit
            to $2,800/month in newsletters. The blueprints are absurdly specific."
          </blockquote>
          <div className="mt-6 text-sm opacity-80">— Devon K., ajobforsure Pro member</div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 py-20 text-center">
        <h2 className="font-display text-3xl sm:text-5xl font-black leading-tight">
          Your side hustle is waiting.
        </h2>
        <p className="text-lg text-[var(--stone)] mt-4">Free forever for basic matches. Pro unlocks the blueprints.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button onClick={startQuiz} className="btn-copper" data-testid="cta-start-quiz-btn">
            Find my hustle <ArrowRight size={18}/>
          </button>
          <button onClick={()=>nav("/pricing")} className="btn-outline" data-testid="cta-see-pricing-btn">
            See Pro plans
          </button>
        </div>
      </section>

      <footer className="border-t border-[var(--border-warm)] py-8 text-center text-sm text-[var(--stone)]">
        ajobforsure © {new Date().getFullYear()} · Built for people who actually want to earn.
      </footer>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode="signup" onSuccess={()=>{setAuthOpen(false); nav("/quiz");}}/>
    </div>
  );
}
